// hooks.ts — React Query hooks for the Animus panel.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, setCsrf } from "./client";

// --- types matching the Go-side /api/v1/* JSON ---

export type Role = "auditor" | "operator" | "admin";

export interface User {
  username: string;
  role: Role;
}

export interface MeResponse {
  username: string;
  role: Role;
  csrf: string;
}

export interface ReceiptRow {
  id: string;
  uploaded_at_sec: number;
  size_bytes: number;
  source_ip: string;
  device_id: string;
  session_hash: string; // hex
  hmac_sig: string;     // hex
  verified: boolean;
  decision_count: number;
}

export interface ReceiptList {
  items: ReceiptRow[];
  next_cursor: string;
  total?: number;
}

export interface ReceiptDetail extends ReceiptRow {
  raw_bundle: string;
}

export interface VerifyResponse {
  verified: boolean;
  key_hex: string;
  session_hash: string;
  hmac_sig: string;
  reference: string;
}

// RegisterReceiptResponse — the shape returned by POST /api/v1/receipts/register
// (mirror of storeAndRespondReceipt in receipts.go).
//
// The HEADLINE proof is device_rooted (ecdsa_valid && config_verified): the
// receipt's ECDSA-P256 signature over session_hash verifies against the signer
// device's enrollment-pinned public key, AND the session_hash commits the
// reported merge config. `verified` (shared-key HMAC) is SECONDARY and reads
// false for canary kits by design — their HMAC key is device-bound and the SA
// does not hold it; the device proof is the ECDSA signature, not the HMAC.
export interface RegisterReceiptResponse {
  id: string;
  verified: boolean; // shared-key HMAC (secondary; false for device-bound kits)
  decision_count: number;
  url: string;
  verify_url: string;
  session_hash: string;
  hmac_sig: string;
  format?: string;
  config_check_applicable?: boolean;
  config_authentic?: boolean;
  preamble_applicable?: boolean;
  preamble_authentic?: boolean;
  config_verified?: boolean;
  // Device-rooted ECDSA verdict (the headline proof).
  ecdsa_valid?: boolean;
  device_rooted?: boolean; // ecdsa_valid && config_verified
  signer_device_id?: string;
  ecdsa_reason?: string;
}

// registerReceipt POSTs a reply's arc.receipt (the self-contained signed
// attestation bundle) to the operator-session-gated transparency-log endpoint.
// The receipt is passed through verbatim (no field transform) — it already IS
// the merge-bundle the SA store accepts. deviceId names the signer kit so the
// SA can ECDSA-verify the receipt against that device's enrollment-pinned
// pubkey (the device-rooted proof). CSRF + session ride the api() wrapper.
export async function registerReceipt(
  receipt: unknown,
  deviceId?: string,
): Promise<RegisterReceiptResponse> {
  const qs = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
  return api<RegisterReceiptResponse>(`/api/v1/receipts/register${qs}`, {
    method: "POST",
    body: receipt,
  });
}

// --- /me query ---

export function useMe() {
  return useQuery<MeResponse | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const m = await api<MeResponse>("/api/v1/me");
        setCsrf(m.csrf);
        return m;
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
}

// --- login / logout mutations ---

export type LoginPhase1Result =
  | { user: User; csrf: string }                              // single-factor success
  | { requires_2fa: true; username: string };                // need /login/totp

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { username: string; password: string }) => {
      const r = await api<LoginPhase1Result>("/api/v1/auth/login", {
        method: "POST",
        body: input,
        skipCsrf: true,
      });
      if ("csrf" in r) setCsrf(r.csrf);
      return r;
    },
    onSuccess: (r) => {
      if ("csrf" in r) qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useLoginTOTP() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string }) => {
      const r = await api<{ user: User; csrf: string }>("/api/v1/auth/login/totp", {
        method: "POST",
        body: input,
        skipCsrf: true,
      });
      setCsrf(r.csrf);
      return r;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api<void>("/api/v1/auth/logout", { method: "POST" });
      setCsrf(null);
    },
    onSuccess: () => qc.setQueryData(["me"], null),
  });
}

// --- receipts queries ---

export function useReceipts(cursor: string | null) {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return useQuery<ReceiptList>({
    queryKey: ["receipts", cursor],
    queryFn: () => api<ReceiptList>(`/api/v1/receipts${qs}`),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export function useReceiptDetail(id: string | undefined) {
  return useQuery<ReceiptDetail>({
    queryKey: ["receipt", id],
    queryFn: () => api<ReceiptDetail>(`/api/v1/receipts/${id}`),
    enabled: !!id,
  });
}

export function useReceiptVerify(id: string | undefined) {
  return useQuery<VerifyResponse>({
    queryKey: ["receipt-verify", id],
    queryFn: () => api<VerifyResponse>(`/api/v1/receipts/${id}/verify`),
    enabled: !!id,
  });
}
